import { inject, Injectable, signal } from '@angular/core';

import {
  type ApplicationType as Application,
  type ComputedWebApp,
  FILE_DOCUMENT_TYPE,
  UserService,
} from '@evc/platform';
import { Maybe } from '@evc/web-components';
import { SharedConfigService } from '@shared/services/config/config.service';

import { ApplicationEnum } from '@app/types/application.enum';
import type { FileItem } from '@app/types/file-item.type';

const OPEN_WITH_APP_NAMES = ['windview', 'aeroview'];

@Injectable({
  providedIn: 'root',
})
export class ApplicationsService {
  #configService = inject(SharedConfigService);
  #userService = inject(UserService);
  private oscPrefix: string = this.#configService.get('osc');

  openables = signal<Maybe<ComputedWebApp[]>>(undefined);

  public areOnlyFiles(items: FileItem[]): boolean {
    return !!items.length && items.every((i) => i.type === FILE_DOCUMENT_TYPE.FILE);
  }

  public isOneSelected(items: FileItem[]): boolean {
    return items.length === 1;
  }

  public isOneFileSelected(items: FileItem[]): boolean {
    return this.isOneSelected(items) && items[0].type === FILE_DOCUMENT_TYPE.FILE;
  }

  public isLicenseExpired(app: Application): boolean {
    return !app.exclude && !app.userHasActiveValidLicense;
  }

  public async fetchOpenableApps(): Promise<ComputedWebApp[]> {
    await this.#userService.getUserApplications();
    const openableApps = (this.#userService.applications() ?? []).filter(
      (app) => app.isAvailable && OPEN_WITH_APP_NAMES.includes(app.name?.toLowerCase()),
    );

    this.openables.set(openableApps);
    return openableApps;
  }

  public getAppsForFileSelected(items: FileItem[], applications: Application[]): Application[] {
    if (
      !items ||
      !this.isOneSelected(items) ||
      !this.areOnlyFiles(items) ||
      !items[0].latestVersion
    ) {
      return [];
    }

    return [
      ...this.getApplicationsByMimetype(applications, items[0].latestVersion.contentType),
      {
        code: ApplicationEnum.ZIP,
        name: 'list.menu.zip',
        icon: 'file_download_outlined',
        exclude: true,
      } as Application,
    ];
  }

  public openLink(link: string): void {
    if (!link) {
      return;
    }

    window.open(`${this.oscPrefix}/${link}`, '_blank');
  }

  private getApplicationsByMimetype(applications: Application[], mimetype: string): Application[] {
    return applications.filter((app) => app.supportedFileMimeTypes?.includes(mimetype));
  }
}
